import { useCallback, useEffect, useState } from "react";
import { MapPin, Crosshair, Trash2, Check, Plus, RefreshCw } from "lucide-react";
import { api, type ContextPrefs, type NamedPlace } from "../lib/api";
import { TeachingBanner } from "../components/TeachingBanner";

const PLACE_KINDS = [
  "home", "work", "school", "gym", "grocery", "doctor", "family_home", "recurring",
];

/** /settings/context — Track C Layer 1+2 control surface.
 *
 *  Opt-in location / weather / activity toggles + the named-places
 *  manager (manual add, confirm inferred, delete). Privacy copy is
 *  explicit: context data goes only to the brain-v2 daemon and never
 *  leaves the device unless a future sync is explicitly enabled. */
export function ContextSettings() {
  const [prefs, setPrefs] = useState<ContextPrefs | null>(null);
  const [places, setPlaces] = useState<NamedPlace[]>([]);
  const [reqs, setReqs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .contextSettings()
      .then((d) => {
        setPrefs(d.prefs);
        setPlaces(d.places);
        setReqs(d.supported_requirements);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  const togglePref = async (key: keyof ContextPrefs) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      // Granting location: ask the browser now so the ping has a
      // position to read on its next cycle.
      if (key === "locationEnabled" && next.locationEnabled && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(() => {}, () => {});
      }
      const r = await api.contextSetPrefs({ [key]: next[key] } as Partial<ContextPrefs>);
      setPrefs(r.prefs);
    } catch (e) {
      setError((e as Error).message);
      load();
    }
  };

  const addHere = async (kind: string) => {
    if (!navigator.geolocation) {
      setError("This browser has no geolocation.");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.contextAddPlace({
            name: kind.charAt(0).toUpperCase() + kind.slice(1),
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            kind,
          });
          load();
        } catch (e) {
          setError((e as Error).message);
        } finally {
          setBusy(false);
        }
      },
      (err) => {
        setError(`Location unavailable: ${err.message}`);
        setBusy(false);
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };

  const runInfer = async () => {
    setBusy(true);
    try {
      await api.contextInferPlaces();
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <MapPin className="w-5 h-5" /> Context
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Memso surfaces things at the right time and place. This page controls
          what context signals it may use. Everything here is opt-in.
        </p>
      </header>

      <TeachingBanner id="context_settings_overview" />

      {error && (
        <div className="rounded-lg bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 text-sm px-3 py-2">
          {error}
        </div>
      )}

      <section className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 space-y-3">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          Permissions
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Location, weather, and activity are off by default. When enabled,
          this data goes only to your Memso daemon on this machine. It never
          leaves the device unless you explicitly enable sync later.
        </p>
        {prefs ? (
          <div className="space-y-2">
            <PrefToggle
              label="Location sharing"
              hint="Lets reminders fire near a place (groceries, doctor) and resolve Home / Work."
              on={prefs.locationEnabled}
              onClick={() => togglePref("locationEnabled")}
            />
            <PrefToggle
              label="Weather"
              hint="Adds a weather snippet to the Today context strip and brief."
              on={prefs.weatherEnabled}
              onClick={() => togglePref("weatherEnabled")}
            />
            <PrefToggle
              label="Activity (mobile)"
              hint="still / walking / driving. Used by commute-aware surfacing. Mobile app only."
              on={prefs.activityEnabled}
              onClick={() => togglePref("activityEnabled")}
            />
          </div>
        ) : (
          <p className="text-sm text-slate-400">Loading…</p>
        )}
      </section>

      <section className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Named places
          </h2>
          <button
            type="button"
            disabled={busy}
            onClick={runInfer}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-semibold px-2.5 py-1.5 disabled:opacity-50"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Detect from history
          </button>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Add a place at your current location, or let Memso infer Home / Work
          from where you spend time. Inferred places are dotted until you
          confirm them.
        </p>

        <div className="flex flex-wrap gap-2">
          {["home", "work", "gym", "grocery", "doctor"].map((k) => (
            <button
              key={k}
              type="button"
              disabled={busy}
              onClick={() => addHere(k)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs font-medium px-2.5 py-1.5 disabled:opacity-50"
            >
              <Crosshair className="w-3.5 h-3.5" /> Add {k} here
            </button>
          ))}
        </div>

        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {places.length === 0 && (
            <li className="text-sm text-slate-400 py-3">No places yet.</li>
          )}
          {places.map((p) => (
            <li key={p.place_id} className="flex items-center gap-3 py-2.5">
              <span
                className={`w-2 h-2 rounded-full ${
                  p.is_inferred
                    ? "border border-dashed border-amber-500"
                    : "bg-emerald-500"
                }`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                  {p.name}{" "}
                  <span className="text-xs text-slate-400">· {p.kind}</span>
                  {p.is_inferred ? (
                    <span className="ml-2 text-[11px] text-amber-600 dark:text-amber-400">
                      inferred
                    </span>
                  ) : null}
                </div>
                <div className="text-[11px] text-slate-400">
                  {p.lat.toFixed(4)}, {p.lng.toFixed(4)} · r{Math.round(p.radius_m)}m
                </div>
              </div>
              {p.is_inferred ? (
                <button
                  type="button"
                  onClick={async () => {
                    await api.contextConfirmPlace(p.place_id);
                    load();
                  }}
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2 py-1"
                >
                  <Check className="w-3.5 h-3.5" /> Confirm
                </button>
              ) : null}
              <button
                type="button"
                aria-label={`Delete ${p.name}`}
                onClick={async () => {
                  await api.contextDeletePlace(p.place_id);
                  load();
                }}
                className="text-slate-400 hover:text-rose-600"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 space-y-2">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Supported timing rules
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Reminders and watchlist items can require one of these context
          conditions before Memso surfaces them:
        </p>
        <div className="flex flex-wrap gap-1.5">
          {reqs.map((r) => (
            <code
              key={r}
              className="text-[11px] rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-slate-600 dark:text-slate-300"
            >
              {r}
            </code>
          ))}
        </div>
      </section>
    </div>
  );
}

function PrefToggle({
  label,
  hint,
  on,
  onClick,
}: {
  label: string;
  hint: string;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-start gap-3 text-left rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/60 px-2 py-2"
    >
      <span
        className={`mt-0.5 w-9 h-5 rounded-full flex-shrink-0 transition-colors ${
          on ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"
        }`}
      >
        <span
          className={`block w-4 h-4 bg-white rounded-full mt-0.5 transition-transform ${
            on ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">
          {label}
        </span>
        <span className="block text-xs text-slate-500 dark:text-slate-400">
          {hint}
        </span>
      </span>
    </button>
  );
}
